import React, { useEffect, useMemo, useRef, useState } from "react";
import { FiChevronDown, FiChevronUp, FiCheck, FiX } from "react-icons/fi";
import "./HarrisChainOfThought.scss";

const formatElapsed = (seconds) => {
    if (!Number.isFinite(seconds) || seconds < 0) return "0s";
    if (seconds < 60) return `${seconds}s`;
    const mins = Math.floor(seconds / 60);
    const rem = seconds % 60;
    return `${mins}m ${rem}s`;
};

/**
 * `collapsed` folds the steps into one line naming the step in progress. The
 * reply has started by then, so the trail gives way to it, but the work is not
 * finished and the line stays until it is. Clicking the line opens the steps
 * again; the chevron in the header folds them back.
 */
const HarrisChainOfThought = ({ steps = [], startedAt = null, collapsed = false }) => {
    const normalizedSteps = useMemo(
        () =>
            steps.map((step) =>
                typeof step === "string"
                    ? { title: step, items: [], state: "active" }
                    : {
                        title: step?.title ?? "",
                        items: step?.items ?? [],
                        state: step?.state ?? "active",
                    }
            ),
        [steps]
    );

    const [open, setOpen] = useState(
        normalizedSteps.map((_, idx) => idx === normalizedSteps.length - 1)
    );

    useEffect(() => {
        setOpen(normalizedSteps.map((_, idx) => idx === normalizedSteps.length - 1));
    }, [normalizedSteps]);

    // Opened by hand while collapsed. Forgotten when the trail expands on its
    // own, so the next turn folds again.
    const [reopened, setReopened] = useState(false);
    useEffect(() => {
        if (!collapsed) setReopened(false);
    }, [collapsed]);
    const folded = collapsed && !reopened;

    const [elapsedSeconds, setElapsedSeconds] = useState(0);
    useEffect(() => {
        if (!startedAt) {
            setElapsedSeconds(0);
            return;
        }
        const tick = () => {
            const now = Date.now();
            setElapsedSeconds(Math.max(0, Math.floor((now - startedAt) / 1000)));
        };
        tick();
        const intervalId = setInterval(tick, 1000);
        return () => clearInterval(intervalId);
    }, [startedAt]);

    // The list scrolls rather than growing, so keep the newest step in view.
    // Watched by height, not by step count: the last step grows after it
    // arrives, as its detail lines render and its spinner resolves.
    const stepsRef = useRef(null);
    const innerRef = useRef(null);
    useEffect(() => {
        const box = stepsRef.current;
        const inner = innerRef.current;
        if (!box || !inner) return undefined;

        const pin = () => {
            box.scrollTop = box.scrollHeight;
        };
        pin();

        if (typeof ResizeObserver === "undefined") return undefined;
        const observer = new ResizeObserver(pin);
        observer.observe(inner);
        return () => observer.disconnect();
    }, []);

    if (!normalizedSteps.length) return null;

    const toggle = (idx) => {
        setOpen((prev) => prev.map((isOpen, i) => (i === idx ? !isOpen : isOpen)));
    };

    const activeIndex = normalizedSteps.length - 1;
    const activeStep = normalizedSteps[activeIndex];
    const progress = `${activeIndex + 1}/${normalizedSteps.length}`;
    const activeDetail = activeStep.items[activeStep.items.length - 1];

    return (
        <div className={`harris-cot${folded ? " is-folded" : ""}`}>
            {folded ? (
                <button
                    type="button"
                    className="cot-bar"
                    onClick={() => setReopened(true)}
                    aria-expanded={false}
                    aria-label={`Show steps: ${activeStep.title}`}
                >
                    <span className="cot-bar__left">
                        <span className="cot-node is-active">
                            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="cot-spinner">
                                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeDasharray="32" strokeLinecap="round" />
                            </svg>
                        </span>
                        <span className="cot-bar__text">
                            {activeStep.title}
                            {activeDetail ? (
                                <span className="cot-bar__detail">{` · ${activeDetail}`}</span>
                            ) : null}
                        </span>
                    </span>
                    <span className="cot-bar__right">
                        <span className="cot-header__progress">{progress}</span>
                        <span className="cot-header__elapsed">{formatElapsed(elapsedSeconds)}</span>
                        <FiChevronDown className="cot-chevron" aria-hidden="true" />
                    </span>
                </button>
            ) : (
                <div className="cot-header" role="status" aria-live="polite">
                    <div className="cot-header__left">
                        <span className="cot-header__title">Working on your request</span>
                    </div>
                    <div className="cot-header__right">
                        <span className="cot-header__progress">{progress}</span>
                        <span className="cot-header__elapsed">{formatElapsed(elapsedSeconds)}</span>
                        {collapsed ? (
                            <button
                                type="button"
                                className="cot-header__fold"
                                onClick={() => setReopened(false)}
                                aria-label="Hide steps"
                            >
                                <FiChevronUp aria-hidden="true" />
                            </button>
                        ) : null}
                    </div>
                </div>
            )}
            <div className="cot-fold" aria-hidden={folded || undefined}>
            <div className="cot-fold__inner">
            <div className="cot-steps" ref={stepsRef}>
            <div className="cot-steps__inner" ref={innerRef}>
            {normalizedSteps.map((step, index) => {
                const hasItems = step.items && step.items.length > 0;
                const isOpen = open[index];
                const isActiveStep = index === activeIndex;
                const stepState = step.state || (isActiveStep ? "active" : "done");
                const isPending = !isActiveStep && stepState !== "done" && stepState !== "error";

                return (
                    <div className="cot-step" key={index}>
                        <div className="cot-left">
                            <span
                                className={`cot-node ${isActiveStep ? "is-active" : ""} ${stepState === "done" ? "is-done" : ""
                                    } ${stepState === "error" ? "is-error" : ""} ${isPending ? "is-pending" : ""
                                    }`}
                            >
                                {stepState === "done" && <FiCheck className="cot-node-icon" />}
                                {isActiveStep && (
                                    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="cot-spinner">
                                        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeDasharray="32" strokeLinecap="round" />
                                    </svg>
                                )}
                                {stepState === "error" && <FiX className="cot-node-icon" />}
                            </span>
                            {index < normalizedSteps.length - 1 && <span className="cot-connector" />}
                        </div>
                        <div
                            className={`cot-body ${isActiveStep ? "is-active" : ""} ${stepState === "done" ? "is-done" : ""
                                } ${stepState === "error" ? "is-error" : ""} ${isPending ? "is-pending" : ""
                                }`}
                        >
                            <button
                                type="button"
                                className="cot-trigger is-toggle"
                                onClick={() => toggle(index)}
                                aria-expanded={isOpen}
                                aria-label="Toggle step"
                            >
                                <span>{step.title}</span>
                                {isOpen ? (
                                    <FiChevronUp className="cot-chevron" />
                                ) : (
                                    <FiChevronDown className="cot-chevron" />
                                )}
                            </button>
                            {hasItems && isOpen && (
                                <div className="cot-content">
                                    {step.items.map((item, itemIdx) => (
                                        <div
                                            className={`cot-item ${isActiveStep ? "is-animating" : ""}`}
                                            key={itemIdx}
                                        >
                                            {item}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                );
            })}
            </div>
            </div>
            </div>
            </div>
        </div>
    );
};

export default HarrisChainOfThought;
